import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Plus, Edit, Trash2, AlertCircle, CheckCircle, X, Search, Save, Globe } from 'lucide-react';
import { holidaysAPI } from '../services/api';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { englishToNepaliDate } from '../utils/dateUtils';

const HolidayManager = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [showNepali, setShowNepali] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [weekendDays, setWeekendDays] = useState([]); // [6] for Saturday
  const [formData, setFormData] = useState({
    date: '',
    nepaliDate: '',
    name: '',
    nameNepali: ''
  });

  const nepaliMonths = [
    'बैशाख', 'जेठ', 'आषाढ', 'श्रावण', 'भाद्र', 'आश्विन',
    'कार्तिक', 'मंसिर', 'पुष', 'माघ', 'फाल्गुन', 'चैत्र'
  ];

  const englishMonths = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const response = await holidaysAPI.getHolidaysForMonth(selectedYear, selectedMonth);
      setHolidays(response.data.data || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error('Failed to fetch holidays');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchHolidays();
    const fetchWeekendConfig = async () => {
      try {
        const res = await holidaysAPI.getWeekendConfig();
        const days = res.data?.weekendDays || [];
        setWeekendDays(days);
      } catch (error) {
        // silent
      }
    };
    fetchWeekendConfig();
  }, [fetchHolidays]);

  const toggleWeekendDay = (day) => {
    setWeekendDays((prev) => (
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    ));
  };

  const saveWeekendConfig = async () => {
    try {
      await holidaysAPI.updateWeekendConfig(weekendDays);
      toast.success(showNepali ? 'साप्ताहिक बिदा सेटिङ अपडेट भयो' : 'Weekend settings updated');
    } catch (error) {
      toast.error(error.response?.data?.message || (showNepali ? 'अपडेट गर्न असफल' : 'Failed to update'));
    }
  };
   
  const handleCreateHoliday = async () => {
    try {
      const payload = { ...formData };
      await holidaysAPI.createHoliday(payload);
      toast.success('Holiday created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create holiday');
    }
  };

  const handleUpdateHoliday = async () => {
    try {
      const payload = { ...formData };
      await holidaysAPI.updateHoliday(editingHoliday.id, payload);
      toast.success('Holiday updated successfully');
      setShowCreateModal(false); 
      setEditingHoliday(null);
      resetForm();
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update holiday');
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (window.confirm('Are you sure you want to delete this holiday?')) {
      try {
        await holidaysAPI.deleteHoliday(id);
        toast.success('Holiday deleted successfully');
        fetchHolidays();
      } catch (error) {
        toast.error('Failed to delete holiday');
      }
    }
  };

  const resetForm = () => {
    setFormData({ date: '', nepaliDate: '', name: '', nameNepali: '' });
  };
  
  const openCreateModal = () => {
      resetForm();
      setEditingHoliday(null);
      setShowCreateModal(true);
  };

  const openEditModal = (holiday) => {
      setEditingHoliday(holiday);
      setFormData({
          date: holiday.date,
          nepaliDate: holiday.nepaliDate,
          name: holiday.name,
          nameNepali: holiday.nameNepali
      });
      setShowCreateModal(true);
  };

  const handleEnglishDateChange = (val) => {
      const nepali = englishToNepaliDate(val);
      setFormData({ ...formData, date: val, nepaliDate: nepali });
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <span className="p-2 bg-red-50 rounded-lg"><Calendar className="w-6 h-6 text-red-600" /></span>
             {showNepali ? 'बिदाहरू व्यवस्थापन' : 'Holiday Management'}
          </h1>
          <p className="mt-1 text-gray-500 text-sm pl-12">
             {showNepali ? 'मासिक बिदाहरू र साप्ताहिक बिदाहरू सेट गर्नुहोस्' : 'Manage monthly calendars and weekend configurations'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
             <button
               onClick={() => setShowNepali(!showNepali)}
               className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm"
             >
               <Globe className="w-4 h-4 mr-2 text-gray-500" />
               {showNepali ? 'English View' : 'नेपाली भ्यू'}
             </button>
             <button
                onClick={openCreateModal}
                className="flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 shadow-sm"
             >
                <Plus className="w-4 h-4 mr-2" />
                {showNepali ? 'नयाँ बिदा' : 'Add Holiday'}
             </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Panel: Filters & Weekend Config */}
        <div className="lg:col-span-1 space-y-6">
             {/* Date Selection Card */}
             <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-6">
                 <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-gray-900 rounded-md flex items-center justify-center shadow-sm">
                        <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900">{showNepali ? 'फिल्टर' : 'Date Selection'}</h3>
                        <p className="text-xs text-gray-600">Filter holidays by period</p>
                    </div>
                 </div>
                 <div className="space-y-4">
                     <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Year</label>
                        <select 
                            value={selectedYear} 
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-gray-200 font-medium text-gray-900 shadow-sm"
                        >
                            {[2023, 2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                     </div>
                     <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Month</label>
                        <select 
                            value={selectedMonth} 
                            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                            className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-gray-200 font-medium text-gray-900 shadow-sm"
                        >
                            {(showNepali ? nepaliMonths : englishMonths).map((m, i) => (
                                <option key={i+1} value={i+1}>{m}</option>
                            ))}
                        </select>
                     </div>
                     <button 
                        onClick={fetchHolidays} 
                        className="w-full bg-gray-900 text-white rounded-md py-3 text-sm font-bold hover:bg-gray-800 shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                     >
                        <Search className="w-4 h-4" />
                        {showNepali ? 'लोड गर्नुहोस्' : 'Load Holidays'}
                     </button>
                 </div>
             </div>

             {/* Weekend Config Card */}
             <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-6">
                 <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-900 rounded-md flex items-center justify-center shadow-sm">
                            <Calendar className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900">{showNepali ? 'साप्ताहिक बिदा' : 'Weekend Days'}</h3>
                            <p className="text-xs text-gray-600">Select weekly off days</p>
                        </div>
                    </div>
                 </div>
                 <div className="space-y-3 mb-4">
                     <label className="flex items-center justify-between p-4 bg-white border-2 border-gray-200 rounded-md hover:border-gray-200 cursor-pointer transition-all group">
                         <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all ${
                                weekendDays.includes(6) 
                                    ? 'bg-gray-900 border-gray-200' 
                                    : 'border-gray-300 group-hover:border-gray-200'
                            }`}>
                                {weekendDays.includes(6) && <CheckCircle className="w-4 h-4 text-white" />}
                            </div>
                            <span className="text-sm font-bold text-gray-900">{showNepali ? 'शनिवार' : 'Saturday'}</span>
                         </div>
                         <input 
                            type="checkbox" 
                            checked={weekendDays.includes(6)}
                            onChange={() => toggleWeekendDay(6)}
                            className="sr-only"
                         />
                     </label>
                     <label className="flex items-center justify-between p-4 bg-white border-2 border-gray-200 rounded-md hover:border-gray-200 cursor-pointer transition-all group">
                         <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all ${
                                weekendDays.includes(0) 
                                    ? 'bg-gray-900 border-gray-200' 
                                    : 'border-gray-300 group-hover:border-gray-200'
                            }`}>
                                {weekendDays.includes(0) && <CheckCircle className="w-4 h-4 text-white" />}
                            </div>
                            <span className="text-sm font-bold text-gray-900">{showNepali ? 'आइतवार' : 'Sunday'}</span>
                         </div>
                         <input 
                            type="checkbox" 
                            checked={weekendDays.includes(0)}
                            onChange={() => toggleWeekendDay(0)}
                            className="sr-only"
                         />
                     </label>
                 </div>
                 <button 
                    onClick={saveWeekendConfig} 
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-md py-3 text-sm font-bold hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                 >
                    <Save className="w-4 h-4" />
                    {showNepali ? 'सेभ गर्नुहोस्' : 'Save Settings'}
                 </button>
             </div>
        </div>
        
        {/* Right Panel: Holiday List */}
        <div className="lg:col-span-2">
            <div className="bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden min-h-[500px]">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-900">
                        {showNepali 
                            ? `${nepaliMonths[selectedMonth-1]} को बिदा सूची` 
                            : `${englishMonths[selectedMonth-1]} Holidays`
                        }
                    </h3>
                    <span className="text-xs font-semibold px-2 py-1 bg-white border border-gray-200 rounded text-gray-500">
                        {holidays.length} Days
                    </span>
                </div>
                
                <div className="p-0">
                    {loading ? (
                        <div className="p-12 text-center">
                             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                        </div>
                    ) : holidays.length === 0 ? (
                        <div className="p-12 text-center text-gray-500">
                            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p>No holidays found for this month.</p>
                        </div>
                    ) : ( 
                        <div className="divide-y divide-gray-100">
                            {holidays.map((holiday) => (
                                <div key={holiday.id} className="p-4 hover:bg-gray-50 flex items-center justify-between group transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="flex flex-col items-center justify-center w-14 h-14 bg-red-50 rounded-lg text-red-700 border border-red-100">
                                            <span className="text-xs font-bold uppercase">{new Date(holiday.date).toLocaleDateString('en-US', {weekday: 'short'})}</span>
                                            <span className="text-base font-semibold">{new Date(holiday.date).getDate()}</span>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 text-lg">
                                                {showNepali ? (holiday.nameNepali || holiday.name) : holiday.name}
                                            </h4>
                                            <div className="flex items-center text-xs text-gray-500 gap-2">
                                                <span>{holiday.date}</span>
                                                <span>•</span>
                                                <span className="font-medium text-gray-600">{holiday.nepaliDate}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button 
                                            onClick={() => openEditModal(holiday)}
                                            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                                        >
                                            <Edit className="w-4 h-4" />
                                        </button>
                                        <button 
                                            onClick={() => handleDeleteHoliday(holiday.id)}
                                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
      </div>

       {/* Create/Edit Modal */}
       <AnimatePresence>
        {showCreateModal && (
            <div className="fixed inset-0 bg-gray-900/70 flex items-center justify-center z-50 backdrop-blur-sm p-4">
                <motion.div 
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden"
                >
                    {/* Header with gradient */}
                    <div className="px-6 py-5 bg-gradient-to-r from-red-500 to-pink-600 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold text-white text-lg">
                                    {editingHoliday 
                                        ? (showNepali ? 'बिदा सम्पादन' : 'Edit Holiday') 
                                        : (showNepali ? 'नयाँ बिदा' : 'New Holiday')}
                                </h3>
                                <p className="text-xs text-white/80">
                                    {editingHoliday ? 'Update holiday details' : 'Add a new holiday to the calendar'}
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={() => setShowCreateModal(false)} 
                            className="text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-2 transition-all"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    
                    {/* Form Content */}
                    <div className="p-6 space-y-5 bg-gradient-to-b from-gray-50 to-white">
                        {/* Date Fields Row */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                    <Calendar className="w-4 h-4 text-red-500" />
                                    English Date
                                </label>
                                <input 
                                    type="date"
                                    value={formData.date}
                                    onChange={(e) => handleEnglishDateChange(e.target.value)}
                                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                    <Globe className="w-4 h-4 text-gray-700" />
                                    Nepali Date
                                </label>
                                <div className="relative">
                                    <input 
                                        type="text"
                                        value={formData.nepaliDate}
                                        readOnly
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 bg-gradient-to-r from-orange-50 to-amber-50 rounded-md text-gray-600 cursor-not-allowed"
                                    />
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
                                        Auto
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Holiday Names */}
                        <div className="space-y-4">
                            <div>
                                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                    <span className="w-5 h-5 bg-gray-100 rounded-lg flex items-center justify-center text-xs font-bold text-gray-700">EN</span>
                                    Holiday Name (English)
                                </label>
                                <input 
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all placeholder:text-gray-400"
                                    placeholder="e.g. Constitution Day"
                                />
                            </div>
                            <div>
                                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                                    <span className="w-5 h-5 bg-gray-100 rounded-lg flex items-center justify-center text-xs font-bold text-gray-700">NP</span>
                                    Holiday Name (Nepali)
                                </label>
                                <input 
                                    type="text"
                                    value={formData.nameNepali}
                                    onChange={(e) => setFormData({...formData, nameNepali: e.target.value})}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all placeholder:text-gray-400"
                                    placeholder="e.g. संविधान दिवस"
                                />
                            </div>
                        </div>

                        {/* Info Box */}
                        <div className="flex items-start gap-3 p-4 bg-gray-100 border border-gray-200 rounded-md">
                            <AlertCircle className="w-5 h-5 text-gray-700 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-gray-700">
                                <p className="font-medium mb-1">Quick Tip</p>
                                <p className="text-xs text-gray-900">The Nepali date is automatically calculated when you select an English date.</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Footer Actions */}
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                        <button 
                            onClick={() => setShowCreateModal(false)}
                            className="px-5 py-2.5 text-gray-700 bg-white hover:bg-gray-100 border-2 border-gray-200 rounded-md font-medium transition-all hover:shadow-sm"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={editingHoliday ? handleUpdateHoliday : handleCreateHoliday}
                            className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-md font-semibold hover:from-red-700 hover:to-pink-700 shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                        >
                            {editingHoliday ? (
                                <><Save className="w-4 h-4" /> Update Holiday</>
                            ) : (
                                <><Plus className="w-4 h-4" /> Create Holiday</>
                            )}
                        </button>
                    </div>
                </motion.div>
            </div>
        )}
       </AnimatePresence>
    </div>
  );
};

export default HolidayManager;
